import type { AISummary, ARWAccount, ARWAccountBand, ProductComparisonMatch } from '@fig/shared';
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { bestPremiumBonus, issueAgeBonusLabel, premiumBandLabel } from '@/lib/rate-bands';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface StrategyAllocation {
  accountName: string;
  accountSourceCode: string | null;
  calculationMethod: string | null;
  rate: number | null;
  rateType: 'cap' | 'participation' | 'fixed' | 'spread' | null;
  percentage: number;
  isSP500PTP: boolean;
}

export interface ProductAllocations {
  productId: string;
  annuityName: string;
  carrierName: string;
  strategies: StrategyAllocation[];
  premiumBandLabel: string | null;
  bonusLabel: string | null;
}

export interface ReviewApprovalData {
  allocations: ProductAllocations[];
  editedSummary: string;
  originalAllocations: ProductAllocations[];
  originalSummary: string;
}

export interface ReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matches: ProductComparisonMatch[];
  aiSummary: AISummary | null;
  isSummaryLoading?: boolean;
  onApprove: (data: ReviewApprovalData) => void | Promise<void>;
  onComplete?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function isSP500PTP(name: string, sourceCode: string | null): boolean {
  const upper = name.toUpperCase();
  const source = (sourceCode ?? '').toUpperCase();
  const isSP =
    upper.includes('S&P') ||
    upper.includes('SP500') ||
    source.includes('SP500') ||
    source.includes('S&P');
  const isPTP = upper.includes('POINT') || upper.includes('PTP') || upper.includes('ANNUAL');
  return isSP && isPTP;
}

function getBestRate(bands: ARWAccountBand[]): {
  rate: number | null;
  type: 'cap' | 'participation' | 'fixed' | 'spread' | null;
} {
  let bestCap = 0;
  let bestPart = 0;
  let bestFixed = 0;
  for (const band of bands) {
    for (const r of band.rates ?? []) {
      if (r.cap != null && r.cap > bestCap) bestCap = r.cap;
      if (r.partRate != null && r.partRate > bestPart) bestPart = r.partRate;
      if (r.fixedRate != null && r.fixedRate > bestFixed) bestFixed = r.fixedRate;
    }
  }
  if (bestCap > 0) return { rate: bestCap, type: 'cap' };
  if (bestPart > 0) return { rate: bestPart, type: 'participation' };
  if (bestFixed > 0) return { rate: bestFixed, type: 'fixed' };
  return { rate: null, type: null };
}

function buildAllocations(match: ProductComparisonMatch): ProductAllocations {
  const accounts = (match.accounts ?? []) as ARWAccount[];
  const strategies: StrategyAllocation[] = accounts.map((acct) => {
    const sp = isSP500PTP(acct.name ?? '', acct.accountSourceCode ?? null);
    const best = getBestRate(acct.bands ?? []);
    return {
      accountName: acct.name ?? 'Unknown Strategy',
      accountSourceCode: acct.accountSourceCode ?? null,
      calculationMethod: acct.calculationMethod ?? null,
      rate: best.rate,
      rateType: best.type,
      percentage: 0,
      isSP500PTP: sp,
    };
  });

  strategies.sort((a, b) => {
    if (a.isSP500PTP && !b.isSP500PTP) return -1;
    if (!a.isSP500PTP && b.isSP500PTP) return 1;
    return (b.rate ?? 0) - (a.rate ?? 0);
  });

  const sp500Idx = strategies.findIndex((s) => s.isSP500PTP);
  if (sp500Idx >= 0) {
    strategies[sp500Idx].percentage = 100;
  } else if (strategies.length > 0) {
    strategies[0].percentage = 100;
  }

  const bonusRate = bestPremiumBonus(match.premiumBonuses)?.theRate ?? 0;
  const bonusTier = issueAgeBonusLabel(match.premiumBonuses);
  const bonusLabel =
    bonusRate > 0 && bonusTier
      ? `${bonusRate}% · ${bonusTier}`
      : bonusRate > 0
        ? `${bonusRate}%`
        : bonusTier;

  return {
    productId: match.productId,
    annuityName: match.annuityName,
    carrierName: match.carrierName,
    strategies,
    premiumBandLabel: premiumBandLabel(match.accounts),
    bonusLabel,
  };
}

function formatSummaryText(aiSummary: AISummary | null): string {
  if (!aiSummary) return '';

  return [
    aiSummary.summary,
    '',
    ...(aiSummary.insights.length > 0
      ? ['Key Insights:', ...aiSummary.insights.map((ins) => `- ${ins}`)]
      : []),
    '',
    aiSummary.recommendation,
  ]
    .join('\n')
    .trim();
}

function formatRate(rate: number | null, type: string | null): string {
  if (rate == null) return '--';
  if (type === 'participation') return `${rate}% participation`;
  if (type === 'fixed') return `${rate.toFixed(2)}% fixed`;
  return `${rate.toFixed(2)}% cap`;
}

function totalAllocation(strategies: StrategyAllocation[]): number {
  return strategies.reduce((sum, s) => sum + s.percentage, 0);
}

// ============================================================================
// Sub-components
// ============================================================================

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={cn(
            'h-1.5 rounded-full transition-all duration-200',
            i === current ? 'w-6 bg-primary' : i < current ? 'w-3 bg-primary/40' : 'w-3 bg-border'
          )}
        />
      ))}
    </div>
  );
}

interface StrategyEditorProps {
  allocation: ProductAllocations;
  onChange: (strategies: StrategyAllocation[]) => void;
}

function StrategyEditor({ allocation, onChange }: StrategyEditorProps) {
  const [showAll, setShowAll] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<number, string>>({});
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const total = totalAllocation(allocation.strategies);
  const isValid = total === 100;
  const isOverAllocated = total > 100;

  useEffect(() => {
    setShowAll(false);
    setDraftValues({});
  }, [allocation.productId]);

  const visible = useMemo(() => {
    const strategiesWithIndex = allocation.strategies.map((strategy, realIndex) => ({
      strategy,
      realIndex,
      inputId: `${allocation.productId}-${realIndex}`,
    }));

    if (showAll) return strategiesWithIndex;
    return strategiesWithIndex.filter(
      ({ strategy }) => strategy.isSP500PTP || strategy.percentage > 0
    );
  }, [allocation.productId, allocation.strategies, showAll]);

  const hiddenCount = allocation.strategies.length - visible.length;

  const focusAllocationInput = useCallback((realIndex: number) => {
    const input = inputRefs.current[realIndex];
    if (!input) return;

    input.focus();
    input.select();
  }, []);

  const updatePercentage = useCallback(
    (realIndex: number, value: number) => {
      const updated = [...allocation.strategies];
      updated[realIndex] = { ...updated[realIndex], percentage: value };
      onChange(updated);
    },
    [allocation.strategies, onChange]
  );

  const handleInputChange = useCallback(
    (realIndex: number, rawValue: string) => {
      if (!/^\d{0,3}$/.test(rawValue)) return;

      setDraftValues((prev) => ({ ...prev, [realIndex]: rawValue }));

      if (rawValue === '') return;

      updatePercentage(realIndex, Math.max(0, Math.min(100, Number(rawValue))));
    },
    [updatePercentage]
  );

  const handleInputBlur = useCallback(
    (realIndex: number) => {
      const draftValue = draftValues[realIndex];
      if (draftValue == null) return;

      const nextValue =
        draftValue.trim() === '' ? 0 : Math.max(0, Math.min(100, Number(draftValue)));

      updatePercentage(realIndex, nextValue);
      setDraftValues((prev) => ({ ...prev, [realIndex]: String(nextValue) }));
    },
    [draftValues, updatePercentage]
  );

  const handleInputKeyDown = useCallback(
    (visibleIndex: number, event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const target = visible[visibleIndex + direction];
      if (!target) return;

      focusAllocationInput(target.realIndex);
    },
    [focusAllocationInput, visible]
  );

  const allocationTone = isValid
    ? {
        className: 'border-success/30 bg-success/10 text-success',
        helperText: 'Ready to continue',
      }
    : isOverAllocated
      ? {
          className: 'border-destructive/30 bg-destructive/10 text-destructive',
          helperText: `${total - 100}% over allocated`,
        }
      : {
          className: 'border-border bg-muted/40 text-muted-foreground',
          helperText: `${100 - total}% left to allocate`,
        };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
        <div
          className="rounded-lg border p-4 shadow-elevation-1"
          style={{
            backgroundColor: 'hsl(var(--surface-2))',
            borderColor: 'hsl(var(--border))',
          }}
        >
          <h3 className="text-sm font-semibold text-foreground">{allocation.annuityName}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{allocation.carrierName}</p>
          {(allocation.premiumBandLabel || allocation.bonusLabel) && (
            <p className="mt-1 text-xs text-muted-foreground">
              {[allocation.premiumBandLabel, allocation.bonusLabel].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <div
          className={cn(
            'flex flex-col justify-center rounded-lg border px-4 py-3',
            allocationTone.className
          )}
        >
          <span className="text-[11px] font-medium uppercase tracking-wider">Total Allocation</span>
          <span className="mt-1 text-2xl font-semibold font-tabular">{total}%</span>
          <p className="mt-1 text-xs leading-relaxed">
            {allocationTone.helperText}
            {!isValid && ' · final total must equal 100%'}
          </p>
        </div>
      </div>

      {/* Strategy rows — divider-separated, no individual cards */}
      <div>
        <p className="mb-2 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Crediting Strategies
        </p>
        {visible.map(({ strategy, realIndex, inputId }, i) => (
          <div key={inputId}>
            {i > 0 && <div className="mx-1 border-t border-border" />}
            <div className="flex items-center gap-3 px-3 py-3 text-sm">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <TruncatedTooltip
                    text={strategy.accountName}
                    className="font-medium text-foreground"
                  />
                  {strategy.isSP500PTP && (
                    <Badge variant="default" className="text-[10px] px-1.5 py-0 shrink-0">
                      S&P 500
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatRate(strategy.rate, strategy.rateType)}
                  {strategy.calculationMethod ? ` · ${strategy.calculationMethod}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={draftValues[realIndex] ?? String(strategy.percentage)}
                  ref={(node) => {
                    inputRefs.current[realIndex] = node;
                  }}
                  onChange={(e) => handleInputChange(realIndex, e.target.value)}
                  onBlur={() => handleInputBlur(realIndex)}
                  onFocus={(e) => e.currentTarget.select()}
                  onKeyDown={(event) => handleInputKeyDown(i, event)}
                  aria-label={`${strategy.accountName} allocation percentage`}
                  className="h-7 w-14 rounded-md border border-input bg-background text-center text-sm font-tabular text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  style={{ backgroundColor: 'hsl(var(--surface-0))' }}
                />
                <span className="w-3 text-xs text-muted-foreground">%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show more / fewer */}
      {hiddenCount > 0 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="flex items-center gap-1 px-1 text-xs text-primary transition-colors hover:text-primary/80"
        >
          <ChevronDown className="h-3 w-3" />
          Show {hiddenCount} more {hiddenCount === 1 ? 'strategy' : 'strategies'}
        </button>
      )}
      {showAll && allocation.strategies.length > 2 && (
        <button
          onClick={() => setShowAll(false)}
          className="flex items-center gap-1 px-1 text-xs text-primary transition-colors hover:text-primary/80"
        >
          <ChevronUp className="h-3 w-3" />
          Show fewer
        </button>
      )}
    </div>
  );
}

function ProductReviewCard({ allocation }: { allocation: ProductAllocations }) {
  const active = allocation.strategies.filter((s) => s.percentage > 0);

  return (
    <div
      className="space-y-2.5 rounded-lg border p-4 shadow-elevation-2"
      style={{
        backgroundColor: 'hsl(var(--surface-2))',
        borderColor: 'hsl(var(--border))',
      }}
    >
      <div>
        <h4 className="text-sm font-semibold text-foreground">{allocation.annuityName}</h4>
        <p className="text-xs text-muted-foreground">{allocation.carrierName}</p>
        {(allocation.premiumBandLabel || allocation.bonusLabel) && (
          <p className="text-xs text-muted-foreground">
            {[allocation.premiumBandLabel, allocation.bonusLabel].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        {active.map((s, index) => (
          <div
            key={`${s.accountName}-${s.accountSourceCode}-${index}`}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <TruncatedTooltip text={s.accountName} className="text-foreground" />
              {s.isSP500PTP && (
                <Badge variant="default" className="text-[10px] px-1.5 py-0 shrink-0">
                  S&P 500
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <span className="font-tabular text-xs text-muted-foreground">
                {formatRate(s.rate, s.rateType)}
              </span>
              <Badge variant="secondary" className="text-xs font-semibold font-tabular">
                {s.percentage}%
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Success Animation
// ============================================================================

function SuccessCheckmark() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 80 80" className="h-20 w-20 text-success">
          <circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="226"
            strokeDashoffset="226"
            className="animate-[circle-draw_0.4s_ease-out_forwards]"
          />
          <path
            d="M24 42 L35 53 L56 28"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="50"
            strokeDashoffset="50"
            className="animate-[check-draw_0.3s_ease-out_0.35s_forwards]"
          />
        </svg>
      </div>
      <p className="mt-4 text-sm font-medium text-success">Forwarded for Review</p>
    </div>
  );
}

// ============================================================================
// Main Modal
// ============================================================================

export function ReviewModal({
  open,
  onOpenChange,
  matches,
  aiSummary,
  isSummaryLoading = false,
  onApprove,
  onComplete,
}: ReviewModalProps) {
  const productCount = matches.length;
  const totalSteps = productCount + 1;

  const [step, setStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [allocations, setAllocations] = useState<ProductAllocations[]>([]);
  const [originalAllocations, setOriginalAllocations] = useState<ProductAllocations[]>([]);
  const [editedSummary, setEditedSummary] = useState('');
  const [originalSummary, setOriginalSummary] = useState('');
  const [excludeSummary, setExcludeSummary] = useState(false);
  const initializedMatchIdsRef = useRef('');
  const lastAppliedSummaryRef = useRef('');
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const matchIds = matches.map((match) => match.productId).join('|');
    const shouldInitialize =
      open && (!wasOpenRef.current || initializedMatchIdsRef.current !== matchIds);
    wasOpenRef.current = open;

    if (!open || !shouldInitialize) return;

    const initial = matches.map(buildAllocations);
    const summaryText = formatSummaryText(aiSummary);
    initializedMatchIdsRef.current = matchIds;
    lastAppliedSummaryRef.current = summaryText;

    setStep(0);
    setShowSuccess(false);
    setIsApproving(false);
    setAllocations(initial);
    setOriginalAllocations(JSON.parse(JSON.stringify(initial)));
    setEditedSummary(summaryText);
    setOriginalSummary(summaryText);
    setExcludeSummary(false);
  }, [aiSummary, matches, open]);

  useEffect(() => {
    if (!open) return;

    const summaryText = formatSummaryText(aiSummary);
    // Capture the previously applied summary BEFORE mutating the ref. The setEditedSummary
    // updater runs later (during reconciliation), by which point the ref would already hold
    // the new value — so comparing against it there would never match and late-arriving
    // summaries (e.g. one regenerated after the modal opened) would be silently dropped.
    const previousApplied = lastAppliedSummaryRef.current;
    if (summaryText === previousApplied) return;

    lastAppliedSummaryRef.current = summaryText;
    setOriginalSummary(summaryText);
    // Only overwrite the textarea if the advisor hasn't manually edited it.
    setEditedSummary((prev) => (prev === previousApplied ? summaryText : prev));
  }, [aiSummary, open]);

  const handleStrategyChange = useCallback(
    (productIndex: number, strategies: StrategyAllocation[]) => {
      setAllocations((prev) => {
        const updated = [...prev];
        updated[productIndex] = { ...updated[productIndex], strategies };
        return updated;
      });
    },
    []
  );

  const isReviewStep = step === productCount;
  const currentAllocation = !isReviewStep ? allocations[step] : null;
  const currentTotal = currentAllocation ? totalAllocation(currentAllocation.strategies) : 100;
  const canProceed = isReviewStep || currentTotal === 100;
  const allValid = allocations.every((a) => totalAllocation(a.strategies) === 100);

  const handleNext = useCallback(() => {
    if (step < totalSteps - 1) setStep(step + 1);
  }, [step, totalSteps]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep(step - 1);
  }, [step]);

  const handleApprove = useCallback(async () => {
    if (allValid) {
      setIsApproving(true);
      try {
        await onApprove({
          allocations,
          // When the advisor opts to exclude the AI summary, forward an empty summary
          // regardless of what is currently populated in the textarea.
          editedSummary: excludeSummary ? '' : editedSummary,
          originalAllocations,
          originalSummary,
        });
        setShowSuccess(true);
        setTimeout(() => {
          onOpenChange(false);
          onComplete?.();
        }, 1500);
      } catch {
        return;
      } finally {
        setIsApproving(false);
      }
    }
  }, [
    allValid,
    allocations,
    editedSummary,
    excludeSummary,
    originalAllocations,
    originalSummary,
    onApprove,
    onOpenChange,
    onComplete,
  ]);

  if (!open) return null;

  const stepLabel = isReviewStep
    ? 'Review & Approve'
    : `Product ${step + 1} of ${productCount}: Strategy Allocation`;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="fixed inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
        onClick={() => onOpenChange(false)}
      />

      {/* Modal panel — 40% wider (max-w-[56rem] ≈ 896px vs 672px) */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className="relative flex max-h-[85vh] w-full max-w-[64rem] flex-col rounded-lg border border-border bg-card text-card-foreground shadow-elevation-3"
          style={{
            backgroundColor: 'hsl(var(--popover))',
            borderColor: 'hsl(var(--border))',
            color: 'hsl(var(--popover-foreground))',
            boxShadow: 'var(--shadow-3)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">{stepLabel}</h2>
              {!isReviewStep && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Set the crediting strategy allocation for this product
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <StepIndicator current={step} total={totalSteps} />
              <button
                onClick={() => onOpenChange(false)}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M1 1L13 13M1 13L13 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {showSuccess ? (
            <div className="flex-1 flex items-center justify-center">
              <SuccessCheckmark />
            </div>
          ) : (
            <>
              {/* Scrollable content */}
              <div
                className="flex-1 overflow-y-auto px-6 py-5"
                style={{ backgroundColor: 'hsl(var(--surface-1))' }}
              >
                {!isReviewStep && currentAllocation ? (
                  <StrategyEditor
                    allocation={currentAllocation}
                    onChange={(strategies) => handleStrategyChange(step, strategies)}
                  />
                ) : (
                  <div className="space-y-5">
                    {allocations.map((alloc) => (
                      <ProductReviewCard key={alloc.productId} allocation={alloc} />
                    ))}

                    {/* Strategy Summary */}
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-foreground">
                            Strategy Summary
                          </h4>
                          {isSummaryLoading && !excludeSummary && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Generating the AI summary. Approval is available once it is ready.
                            </p>
                          )}
                        </div>
                        <label className="flex shrink-0 cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={excludeSummary}
                            onChange={(e) => setExcludeSummary(e.target.checked)}
                            className="h-4 w-4 rounded border-border accent-primary"
                          />
                          Exclude AI summary
                        </label>
                      </div>
                      <textarea
                        value={editedSummary}
                        onChange={(e) => setEditedSummary(e.target.value)}
                        disabled={excludeSummary}
                        rows={10}
                        className={cn(
                          'w-full resize-y rounded-md border border-input bg-background px-3 py-3 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                          excludeSummary && 'cursor-not-allowed opacity-50'
                        )}
                        style={{ backgroundColor: 'hsl(var(--surface-0))' }}
                        placeholder={
                          isSummaryLoading
                            ? 'Strategy summary will appear here when it is ready...'
                            : 'No AI summary available. You can forward without one or add notes manually.'
                        }
                      />
                      {excludeSummary && (
                        <p className="text-xs text-muted-foreground">
                          The AI summary will not be included when this recommendation is forwarded.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-border px-6 py-4">
                <Button variant="outline" size="default" onClick={handleBack} disabled={step === 0}>
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </Button>

                {isReviewStep ? (
                  <Button
                    size="default"
                    onClick={handleApprove}
                    disabled={!allValid || isApproving || (isSummaryLoading && !excludeSummary)}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {isApproving ? 'Forwarding...' : 'Approve and Forward for Review'}
                  </Button>
                ) : (
                  <Button size="default" onClick={handleNext} disabled={!canProceed}>
                    Next
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
